import { useState } from "react";
import { ArrowLeft, Printer, Link as LinkIcon, Check, CaretDown, CaretRight } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

export default function StrategicReportPage() {
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-[#e0e0e0]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-md border-b border-white/5 px-6 py-3 flex items-center justify-between print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex items-center gap-3">
          <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm hover:bg-white/10 transition-colors" data-testid="copy-link-btn">
            {copied ? <Check size={14} className="text-teal-400" /> : <LinkIcon size={14} />}
            {copied ? "Lien copie!" : "Copier le lien"}
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/20 transition-colors" data-testid="print-btn">
            <Printer size={14} /> Imprimer / PDF
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 print:py-4 print:px-8 print:max-w-none">
        {/* Cover */}
        <div className="text-center mb-16 print:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-xs font-medium tracking-widest uppercase mb-6">
            Confidentiel — Fondateurs uniquement
          </div>
          <h1 className="text-5xl sm:text-6xl font-black font-['Outfit'] tracking-tight text-white leading-tight print:text-4xl">
            KNORIA
          </h1>
          <p className="text-lg text-white/40 mt-2 tracking-[0.3em] uppercase font-light">Knowledge Empowered</p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto my-8" />
          <h2 className="text-2xl font-bold font-['Outfit'] text-white/80 print:text-xl">Analyse Strategique Complété</h2>
          <p className="text-white/30 mt-2">Rapport Fondateurs — Avril 2026</p>
          <p className="text-white/20 text-sm mt-1">Prepare pour Eric Roy & Jean-Sebastien Hetu</p>
        </div>

        {/* TABLE OF CONTENTS */}
        <Section num="0" title="Table des matieres" color="white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              "1. Analyse Technique",
              "2. Analyse Concurrentielle",
              "3. Analyse Business & Revenus",
              "4. Analyse Elon Musk",
              "5. Recommandations Prioritaires",
              "6. Score Global",
              "7. Verdict Final",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/3 rounded-lg">
                <CaretRight size={12} className="text-cyan-400" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* SECTION 1 - TECHNIQUE */}
        <Section num="1" title="Analyse Technique de l'Application" color="cyan">
          <H3>1.1 Architecture & Taille du Code</H3>
          <Table headers={["Composant", "Fichiers", "Lignes de code"]} rows={[
            ["Backend (FastAPI)", "23 fichiers Python", "4,911 lignes"],
            ["→ server.py", "1 fichier", "976 lignes"],
            ["→ routers/ (9 modules)", "10 fichiers", "2,501 lignes"],
            ["→ core/ (12 modules)", "13 fichiers", "1,434 lignes"],
            ["Frontend (React)", "92 fichiers JS/JSX", "12,438 lignes"],
            ["→ Pages", "31 pages", "~10,744 lignes"],
            ["→ Composants", "53 composants", "~1,694 lignes"],
            ["Base de données", "36 collections MongoDB", "2,771 documents"],
            ["TOTAL", "115+ fichiers", "~17,350 lignes"],
          ]} highlight={8} />

          <H3>1.2 Stack Technologique</H3>
          <Table headers={["Couche", "Technologies", "Status"]} rows={[
            ["Frontend", "React 18, Shadcn/UI, TailwindCSS, Framer Motion", "Solide"],
            ["Backend", "FastAPI (Python), Motor (async MongoDB)", "Solide"],
            ["Base de données", "MongoDB 7.x", "Opérationnel"],
            ["IA — Transcription", "OpenAI Whisper (chunking 5MB + backoff)", "Opérationnel"],
            ["IA — Extraction", "GPT-5.2 (Q&A, procedures, scripts)", "Opérationnel"],
            ["IA — Chat RAG", "Embeddings + recherche semantique", "Opérationnel"],
            ["IA — Video Avatar", "fal.ai (MultiTalk/SadTalker)", "Opérationnel"],
            ["Paiements", "Stripe (CAD)", "Opérationnel"],
            ["Emails", "Resend (Mode Test)", "Limite"],
            ["Auth", "JWT + bcrypt + refresh tokens + anti-replay", "Solide"],
            ["Sécurité", "RBAC 29 permissions, Audit Logs, AI Firewall", "Enterprise"],
          ]} />

          <H3>1.3 Fonctionnalites Implementees (65+)</H3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <FeatureBlock title="Pipeline IA (Coeur)" items={[
              "Upload video/audio automatique",
              "Transcription Whisper (chunking 5MB)",
              "Diarisation des locuteurs",
              "Extraction GPT-5.2 structuree",
              "Embeddings vectoriels",
              "Chat RAG avec citations video",
              "Retraitement auto des échecs",
            ]} />
            <FeatureBlock title="Formation & Apprentissage" items={[
              "Base de connaissances full-text",
              "Quiz auto-générés par l'IA",
              "Mode examen avec minuteur",
              "Parcours d'onboarding",
              "Certifications & diplomes",
              "Gamification (points, badges, niveaux)",
              "Coach IA personnel",
              "Détecteur de lacunes IA",
            ]} />
            <FeatureBlock title="B2B SaaS Enterprise" items={[
              "Multi-tenant (isolation org_id)",
              "3 plans Stripe (Starter/Business/Enterprise)",
              "Invitations par email (Resend)",
              "Codes partenaires",
              "Roles: CEO, VP, Admin, Employe",
              "Analytique d'equipe par membre",
            ]} />
            <FeatureBlock title="Plateforme Fondateur" items={[
              "Protection fondateur absolue",
              "29 permissions granulaires",
              "Logs d'audit (153+ événements)",
              "18 Feature Flags configurables",
              "Tableau de bord temps reel",
              "Selecteur d'organisation",
              "Exemption facturation",
            ]} />
          </div>

          <H3>1.4 Vulnerabilites Techniques (CRITIQUES)</H3>
          <Table headers={["Priorite", "Problème", "Impact", "Solution"]} rows={[
            ["P0", "Stockage local — videos sur disque conteneur", "Perte de données au restart", "Migration S3/R2"],
            ["P1", "Pas de cache — chaque requête hit MongoDB", "Lenteur a l'échelle", "Redis"],
            ["P1", "Resend en mode test", "Impossible d'inviter", "Acheter domaine .com"],
            ["P2", "Pas de tests automatises", "Regressions possibles", "Suite Pytest CI/CD"],
            ["P2", "Pas de rate limiting global", "Risque DDoS", "Middleware FastAPI"],
            ["P2", "Pas de backup MongoDB", "Perte catastrophique", "MongoDB Atlas / backup"],
          ]} />
        </Section>

        {/* SECTION 2 - CONCURRENCE */}
        <Section num="2" title="Analyse Concurrentielle" color="violet">
          <H3>2.1 Concurrents Directs (Formation Financiere Québec)</H3>
          <Table headers={["Plateforme", "Prix", "Forces", "vs KNORIA"]} rows={[
            ["TACTICS LMS (Edu Performance)", "~$15-25K/an", "100% quebecois, ISO 27001, bilingue", "PAS d'IA, pas de transcription"],
            ["Lara LMS", "~$5-15K/an", "OACIQ, CPA Québec, College professions", "Formation statique, pas d'IA"],
            ["College des professions fin.", "$1,550-2,050/cours", "Formation PQCH officielle AMF", "Pas un LMS, cours isoles"],
            ["CEI / Parcours", "$2,990/programme", "Courtage hypothecaire", "Pas de plateforme continue"],
          ]} />

          <H3>2.2 Concurrents Indirects (LMS Enterprise Global)</H3>
          <Table headers={["Plateforme", "Prix", "Forces", "vs KNORIA"]} rows={[
            ["Docebo", "$25K-$50K+/an", "IA avancee, integrations enterprise", "Prix 4-5x, pas finance QC"],
            ["360Learning", "~$48K/an ($8/user)", "Social learning, co-authoring", "Pas transcription → knowledge"],
            ["TalentLMS", "~$15K/an", "Abordable, rapide", "IA basique, pas de RAG"],
            ["Sana Learn", "Custom (premium)", "Compliance finance SEC/FINRA", "Tres cher, pas quebecois"],
          ]} />

          <H3>2.3 Positionnement KNORIA</H3>
          <div className="bg-white/3 border border-white/10 rounded-xl p-6 mb-6 font-mono text-sm leading-relaxed whitespace-pre">
{`  PRIX ELEVE ($50K+)
      |
      |   Docebo      Sana Learn
      |     ●            ●
      |
      |         360Learning
      |             ●
      |
      |                          ★ KNORIA
      |      TACTICS               $13,990/an
      |        ●                   IA complete + Québécois
      |
      |   TalentLMS     Lara
      |      ●           ●
      |
  PRIX BAS ($5K)
      └─────────────────────────────────────
    GENERIQUE                  SPECIALISE FINANCE`}
          </div>

          <Callout type="success" title="Avantage Competitif Unique (Moat)">
            <p className="font-bold text-white mb-2">KNORIA est le SEUL LMS au monde qui combine :</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Pipeline video → transcription → extraction IA → base de connaissances automatique</li>
              <li>Chat RAG avec citations video source (timestamp exact)</li>
              <li>Specialise services financiers quebecois (bilingue, AMF-aware)</li>
              <li>Multi-tenant B2B SaaS avec prix accessible ($13,990 vs $50K+ Docebo)</li>
            </ol>
            <p className="mt-3 text-white/60">AUCUN concurrent ne fait ca. Docebo a l'IA mais pas l'extraction video. TACTICS est quebecois mais sans IA. Lara fait la conformite AMF mais c'est un LMS statique.</p>
          </Callout>
        </Section>

        {/* SECTION 3 - BUSINESS */}
        <Section num="3" title="Analyse Business & Revenus" color="emerald">
          <H3>3.1 Marche Cible</H3>
          <Table headers={["Segment", "Taille estimee", "Revenu potentiel"]} rows={[
            ["Courtiers hypothecaires QC", "~5,000+ actifs", "500 firmes × $7K = $3.5M/an"],
            ["Conseillers sécurité financiere", "~32,000 au Québec", "1,000 firmes × $14K = $14M/an"],
            ["Planificateurs financiers (IQPF)", "~5,000", "200 firmes × $14K = $2.8M/an"],
            ["Courtiers immobiliers (OACIQ)", "~20,000+", "800 firmes × $7K = $5.6M/an"],
            ["TAM Québec", "", "$25M/an"],
            ["Canada (hors QC)", "3x Québec", "$75M/an"],
            ["TAM TOTAL CANADA", "", "$100M/an"],
          ]} highlight={6} />

          <H3>3.2 Projections de Revenus</H3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-white/3 border border-white/10 rounded-xl p-5">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Scénario Conservateur</p>
              <Table mini headers={["", "An 1", "An 2", "An 3"]} rows={[
                ["Starter", "15", "40", "80"],
                ["Business", "8", "25", "50"],
                ["Enterprise", "3", "10", "25"],
                ["ARR", "$147K", "$460K", "$950K"],
              ]} highlight={3} />
            </div>
            <div className="bg-cyan-500/5 border border-cyan-500/15 rounded-xl p-5">
              <p className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Scénario Agressif (Mode Elon)</p>
              <Table mini headers={["", "An 1", "An 2", "An 3"]} rows={[
                ["Starter", "30", "100", "250"],
                ["Business", "15", "50", "120"],
                ["Enterprise", "8", "30", "80"],
                ["ARR", "$308K", "$1.1M", "$3.1M"],
              ]} highlight={3} />
            </div>
          </div>

          <H3>3.3 Structure de Couts</H3>
          <Table headers={["Poste", "Mensuel", "Annuel"]} rows={[
            ["Infrastructure (AWS/GCP)", "$500-2,000", "$6K-24K"],
            ["OpenAI API (Whisper + GPT-5.2)", "$200-1,500", "$2.4K-18K"],
            ["MongoDB Atlas", "$60-500", "$720-6K"],
            ["Stripe (2.9% + $0.30)", "Variable", "~3% du revenu"],
            ["Resend (emails)", "$20-80", "$240-960"],
            ["fal.ai (video avatar)", "$100-500", "$1.2K-6K"],
            ["TOTAL INFRA", "$900-4,600", "$11K-55K"],
          ]} highlight={6} />
          <Callout type="info" title="Marge brute estimee : 80-90%">
            <p>Tres SaaS-like. Chaque nouveau client coute presque rien en infrastructure additionnelle.</p>
          </Callout>
        </Section>

        {/* SECTION 4 - ELON MUSK */}
        <Section num="4" title="Analyse Elon Musk — First Principles Thinking" color="amber">
          <Callout type="quote" title="La Question Fondamentale">
            <p className="text-lg italic text-white/90">"Pourquoi les firmes financieres perdent-elles 40% du savoir de leurs meilleurs conseillers quand ils partent?"</p>
            <p className="mt-3">Parce que le savoir est dans la TETE des gens, pas dans un systeme. Les videos de formation sont regardees une fois et oubliees. Les procedures sont dans des PDF que personne ne lit.</p>
            <p className="mt-2 font-bold text-white">KNORIA resout ca en AUTOMATISANT l'extraction du savoir. C'est Tesla pour le savoir d'entreprise.</p>
          </Callout>

          <H3>4.1 Les 5 Principes Elon Musk</H3>
          <div className="space-y-3 mb-8">
            <Principle num="1" title='"Make it work"' status="FAIT" statusColor="emerald" desc="Le pipeline video → transcription → extraction → RAG chat est opérationnel. 5 videos traitées, 125 connaissances extraites, 2,137 segments." />
            <Principle num="2" title='"Make it right"' status="EN COURS" statusColor="amber" desc="Architecture refactoree (83% reduction). Sécurité enterprise. Multi-tenant. Reste: stockage local et tests automatises." />
            <Principle num="3" title='"Make it fast"' status="A FAIRE" statusColor="rose" desc="Pas de cache Redis. Pas de CDN. Requêtes MongoDB non optimisees pour 10,000+ utilisateurs." />
            <Principle num="4" title='"Make it cheap"' status="AVANTAGE" statusColor="emerald" desc="$13,990/an vs $50K+ Docebo = 3-4x moins cher. Marge brute 80-90% pour investir en R&D." />
            <Principle num="5" title='"Scale it"' status="LE DEFI" statusColor="cyan" desc="Pas juste Québec. Pas juste finance. La techno est applicable a TOUT secteur avec savoir tacite: medical, juridique, ventes." />
          </div>

          <H3>4.2 Vision 10x — Ce qui Rendrait KNORIA IMBATTABLE</H3>

          <div className="space-y-4 mb-8">
            <LevelBlock level="1" title="Corrections Immediates" time="0-30 jours" color="emerald" items={[
              "S3 Storage — Migration critique, zero perte de données",
              "Domaine .com — Debloquer Resend, credibilite",
              "Redis Cache — Performance 10x sur les pages frequentes",
              "Backup MongoDB — Protection des données clients",
            ]} />
            <LevelBlock level="2" title="Avantages Concurrentiels" time="30-90 jours" color="blue" items={[
              "Conformité AMF — Parcours certifiants, suivi des heures de formation continue",
              "Integration calendrier — Google Calendar pour rappels formation obligatoire",
              "API publique — Connexion aux outils clients (CRM, HRIS)",
              "Application mobile native — PWA ou React Native, offline-first",
              "White-label — Branding personnalisé par firme",
            ]} />
            <LevelBlock level="3" title="Innovation Disruptive — MODE ELON" time="90-180 jours" color="amber" items={[
              '"Clone IA" de votre meilleur conseiller — Avatar IA entraine sur les vidéos. Les nouveaux "parlent" avec le clone du meilleur vendeur',
              "Simulation de client en temps réel — L'IA joue un client difficile pour entraîner les conseillers. Simulateur de vol pour la vente",
              "Scoring de conformite automatique — Detecter les risques AMF AVANT qu'un probleme arrive",
              "Marketplace de contenu — Les firmes partagent/vendent leurs meilleures formations",
              "Intelligence concurrentielle — Tendances du marche → formations proactives",
            ]} />
            <LevelBlock level="4" title="Domination du Marche" time="6-12 mois" color="rose" items={[
              "Expansion verticale — Immobilier (OACIQ), assurance, comptabilite (CPA), juridique",
              "Expansion geographique — Ontario, Colombie-Britannique, puis USA",
              'Certification KNORIA — Standard de formation reconnu par l\'industrie',
              'Acquisition de données — Devenir le "Bloomberg Terminal" de la formation financiere',
            ]} />
          </div>

          <H3>4.3 La Metaphore Tesla</H3>
          <Table headers={["Tesla", "KNORIA"]} rows={[
            ["Voiture electrique ≠ voiture a essence améliorée", "LMS IA ≠ LMS traditionnel avec PowerPoint"],
            ["Battery = avantage competitif fondamental", "Pipeline Whisper→GPT→RAG = avantage fondamental"],
            ["Autopilot = differenciation impossible a copier", "Clone IA + Simulation = differenciation impossible"],
            ["Supercharger network = effet reseau", "Marketplace de contenu = effet reseau"],
            ["OTA updates = amelioration continue", "Feature Flags = amelioration continue"],
          ]} />
        </Section>

        {/* SECTION 5 - RECOMMANDATIONS */}
        <Section num="5" title="Recommandations Prioritaires" color="cyan">
          <H3>Top 5 Actions Immediates (Cette semaine)</H3>
          <Table headers={["#", "Action", "Cout", "Impact"]} rows={[
            ["1", "Domaine knoria.com configure", "Fait", "Emails, credibilite"],
            ["2", "Migrer videos vers Cloudflare R2", "$0 (free tier)", "Eliminer risque perte données"],
            ["3", "Ajouter Redis cache", "$0 (local)", "Performance 5-10x"],
            ["4", 'Créer landing page "Simulateur ROI"', "2-3 jours", "Outil de vente #1"],
            ["5", "Premier client beta (hors vous deux)", "Réseau", "Validation marche"],
          ]} />

          <H3>Pricing Strategique Recommande</H3>
          <Table headers={["Plan", "Prix/mois", "Prix/an", "Cible"]} rows={[
            ["Solo (NOUVEAU)", "$99", "$990", "Conseiller independant"],
            ["Équipe (Starter)", "$299", "$2,990", "Petite firme 2-10"],
            ["Business", "$699", "$6,990", "Firme moyenne 10-50"],
            ["Enterprise", "$1,399", "$13,990", "Grande firme 50-200"],
            ["Unlimited (NOUVEAU)", "Custom", "$25K+", "Reseau national"],
          ]} />
          <Callout type="info" title='Le plan Solo a $99/mois est le "Model 3" de KNORIA'>
            <p>Il ouvre le marche aux 32,000 conseillers individuels du Québec. Volume massif, acquisition organique.</p>
          </Callout>
        </Section>

        {/* SECTION 6 - SCORE */}
        <Section num="6" title="Score Global KNORIA" color="emerald">
          <div className="space-y-3 mb-6">
            <ScoreBar label="Produit / Fonctionnalites" score={9} detail="65+ features, pipeline IA unique" />
            <ScoreBar label="Positionnement marche" score={9.5} detail="Seul LMS IA video specialise finance QC" />
            <ScoreBar label="Sécurité" score={8.5} detail="RBAC, audit, founder protection, JWT" />
            <ScoreBar label="Design / UX" score={8} detail="Premium dark theme, bilingue, mobile" />
            <ScoreBar label="Pricing" score={8} detail="Bien positionne, manque plan Solo" />
            <ScoreBar label="Architecture technique" score={7} detail="Bien structure, manque cache/S3/tests" />
            <ScoreBar label="Scalabilite" score={5} detail="Stockage local, single instance" />
            <ScoreBar label="Go-to-market" score={3} detail="Pas de site public, pas de demo, pas de SEO" />
          </div>
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6 text-center">
            <p className="text-white/40 text-sm uppercase tracking-wider mb-2">Score Global</p>
            <p className="text-5xl font-black font-['Outfit'] text-white">7.3<span className="text-2xl text-white/40">/10</span></p>
            <p className="text-cyan-400 font-medium mt-2">Excellent produit, besoin de mise en marche</p>
          </div>
        </Section>

        {/* SECTION 7 - VERDICT */}
        <Section num="7" title="Verdict Final — Style Elon" color="amber">
          <div className="bg-gradient-to-br from-amber-500/10 via-[#0a0a0f] to-cyan-500/10 border border-amber-500/20 rounded-2xl p-8 text-center">
            <div className="text-4xl mb-6">⚡</div>
            <blockquote className="text-lg sm:text-xl leading-relaxed text-white/90 font-medium italic max-w-2xl mx-auto">
              "Vous avez construit un produit que personne d'autre au Québec n'a. Le pipeline video → IA → connaissances est votre batterie Tesla. Maintenant, il faut arreter de construire dans le garage et commencer a vendre.
            </blockquote>
            <div className="w-16 h-0.5 bg-amber-500/30 mx-auto my-6" />
            <blockquote className="text-lg sm:text-xl leading-relaxed text-white/90 font-medium italic max-w-2xl mx-auto">
              Les 3 prochains mois determinent tout : domaine .com, premier client payant hors fondateurs, et migration S3. Si vous faites ces 3 choses, vous avez un business a $1M ARR en 24 mois. Si vous continuez a ajouter des features sans vendre, vous avez un hobby tres sophistique."
            </blockquote>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center text-white/20 text-xs mt-16 pb-8 border-t border-white/5 pt-8 print:mt-8">
          <p>Rapport généré le 13 avril 2026 — Agent KNORIA</p>
          <p className="mt-1">Données basées sur l&#39;analyse complète du codebase, de la base de données et de la recherche concurrentielle</p>
          <p className="mt-1">Confidentiel — Eric Roy & Jean-Sebastien Hetu</p>
        </div>
      </div>
    </div>
  );
}

/* --- Sub-components --- */

function Section({ num, title, color, children }) {
  const colors = { cyan: "border-cyan-500/20", violet: "border-violet-500/20", emerald: "border-teal-500/20", amber: "border-amber-500/20", white: "border-white/10" };
  const textColors = { cyan: "text-cyan-400", violet: "text-violet-400", emerald: "text-teal-400", amber: "text-amber-400", white: "text-white/50" };
  if (num === "0") return <div className="mb-12">{children}</div>;
  return (
    <div className={`mb-14 print:mb-8 border-t ${colors[color]} pt-8`}>
      <div className="flex items-center gap-3 mb-6">
        <span className={`text-xs font-mono ${textColors[color]} bg-white/5 px-2 py-1 rounded`}>0{num}</span>
        <h2 className="text-2xl font-bold font-['Outfit'] text-white print:text-xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function H3({ children }) {
  return <h3 className="text-base font-bold font-['Outfit'] text-white/80 mt-6 mb-3 print:text-sm">{children}</h3>;
}

function Table({ headers, rows, highlight, mini }) {
  return (
    <div className="overflow-x-auto mb-6">
      <table className={`w-full ${mini ? "text-xs" : "text-sm"}`}>
        <thead>
          <tr className="border-b border-white/10">
            {headers.map((h, i) => <th key={i} className="text-left py-2 px-3 text-white/40 font-medium text-xs uppercase tracking-wider">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={`border-b border-white/5 ${ri === highlight ? "bg-cyan-500/5 font-bold" : ""}`}>
              {row.map((cell, ci) => <td key={ci} className={`py-2.5 px-3 ${ri === highlight ? "text-cyan-300" : "text-white/70"}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeatureBlock({ title, items }) {
  return (
    <div className="bg-white/3 border border-white/8 rounded-xl p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-white/50 mb-3">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/70">
            <span className="text-teal-400 mt-0.5 flex-shrink-0">✓</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Callout({ type, title, children }) {
  const styles = {
    success: "bg-teal-500/5 border-teal-500/20",
    info: "bg-cyan-500/5 border-cyan-500/20",
    quote: "bg-amber-500/5 border-amber-500/20",
  };
  return (
    <div className={`${styles[type]} border rounded-xl p-5 mb-6`}>
      {title && <p className="font-bold text-sm text-white/90 mb-2">{title}</p>}
      <div className="text-sm text-white/60 leading-relaxed">{children}</div>
    </div>
  );
}

function Principle({ num, title, status, statusColor, desc }) {
  const colors = { emerald: "bg-teal-500/10 text-teal-400 border-teal-500/20", amber: "bg-amber-500/10 text-amber-400 border-amber-500/20", rose: "bg-rose-500/10 text-rose-400 border-rose-500/20", cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" };
  return (
    <div className="flex items-start gap-4 bg-white/3 border border-white/8 rounded-xl p-4">
      <span className="text-white/20 font-mono text-sm mt-0.5">0{num}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-white text-sm">{title}</span>
          <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${colors[statusColor]}`}>{status}</span>
        </div>
        <p className="text-sm text-white/50">{desc}</p>
      </div>
    </div>
  );
}

function LevelBlock({ level, title, time, color, items }) {
  const colors = { emerald: "border-teal-500/20 bg-teal-500/3", blue: "border-blue-500/20 bg-blue-500/3", amber: "border-amber-500/20 bg-amber-500/3", rose: "border-rose-500/20 bg-rose-500/3" };
  const textColors = { emerald: "text-teal-400", blue: "text-blue-400", amber: "text-amber-400", rose: "text-rose-400" };
  return (
    <div className={`border rounded-xl p-5 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-mono font-bold ${textColors[color]}`}>NIVEAU {level}</span>
          <span className="font-bold text-white text-sm">{title}</span>
        </div>
        <span className="text-xs text-white/30">{time}</span>
      </div>
      <ol className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/60">
            <span className={`font-mono text-xs mt-0.5 ${textColors[color]}`}>{Number(level-1)*5 + i + 1}.</span>
            {item}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ScoreBar({ label, score, detail }) {
  const pct = (score / 10) * 100;
  const color = score >= 8 ? "bg-teal-500" : score >= 6 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="bg-white/3 border border-white/8 rounded-lg p-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-white/80">{label}</span>
          <span className="text-sm font-bold font-['Outfit'] text-white">{score}/10</span>
        </div>
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[11px] text-white/30 mt-1">{detail}</p>
      </div>
    </div>
  );
}
